import { useState } from 'react'

import { Box, Table, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import Header from './Header'
import Body from './_Body'
import type { QuoteItem, QuotePhase } from './types'

const QuotePhasesTable = () => {
  const {
    company: { legalEntities, currency },
  } = useCurrentSession()
  const legalEntityMainCurrency =
    legalEntities?.find((legalEntity) => legalEntity.main)?.currency || currency || 'EUR'

  const [standaloneItems, setStandaloneItems] = useState<QuoteItem[]>([])

  const [phases, setPhases] = useState<QuotePhase[]>([
    {
      id: '1',
      name: 'Discovery',
      isExpanded: false,
      items: [
        {
          id: '1-1',
          name: 'Junior Designer',
          hoursOrUnits: 150,
          costPerUnit: 2509,
          billPerUnit: 12000,
          profit: 40,
          itemType: 'role',
        },
      ],
    },
    {
      id: '2',
      name: 'Design',
      isExpanded: false,
      items: [
        {
          id: '2-1',
          name: 'Junior Designer',
          hoursOrUnits: 150,
          costPerUnit: 2509,
          billPerUnit: 12000,
          profit: 40,
          itemType: 'role',
        },
        {
          id: '2-2',
          name: 'Senior Designer',
          hoursOrUnits: 50,
          costPerUnit: 3000,
          billPerUnit: 12000,
          profit: 30,
          itemType: 'role',
        },
        {
          id: '2-3',
          name: 'Software license',
          hoursOrUnits: 30,
          costPerUnit: 2000,
          billPerUnit: 3000,
          profit: 50,
          itemType: 'product',
        },
      ],
    },
    {
      id: '3',
      name: 'Development',
      isExpanded: false,
      items: [],
    },
  ])

  const handleTogglePhaseExpand = (phaseId: string) => {
    setPhases((prev) =>
      prev.map((phase) =>
        phase.id === phaseId ? { ...phase, isExpanded: !phase.isExpanded } : phase
      )
    )
  }

  const handleUpdatePhase = (phaseId: string, updates: Partial<QuotePhase>) => {
    setPhases((prev) =>
      prev.map((phase) => (phase.id === phaseId ? { ...phase, ...updates } : phase))
    )
  }

  const handleUpdateItem = (
    phaseId: string,
    itemId: string,
    updates: Partial<QuotePhase['items'][0]>
  ) => {
    setPhases((prev) =>
      prev.map((phase) =>
        phase.id === phaseId
          ? {
              ...phase,
              items: phase.items.map((item) =>
                item.id === itemId ? { ...item, ...updates } : item
              ),
            }
          : phase
      )
    )
  }

  const handleAddItem = (phaseId: string, itemType: 'role' | 'product') => {
    setPhases((prev) =>
      prev.map((phase) =>
        phase.id === phaseId
          ? {
              ...phase,
              items: [
                ...phase.items,
                {
                  id: `${phaseId}-${Date.now()}`,
                  name: '',
                  hoursOrUnits: 0,
                  costPerUnit: 0,
                  billPerUnit: 0,
                  profit: 0,
                  itemType,
                },
              ],
              isExpanded: true,
            }
          : phase
      )
    )
  }

  const handleDeleteItem = (phaseId: string, itemId: string) => {
    setPhases((prev) =>
      prev.map((phase) =>
        phase.id === phaseId
          ? {
              ...phase,
              items: phase.items.filter((item) => item.id !== itemId),
            }
          : phase
      )
    )
  }

  const handleDeletePhase = (phaseId: string) => {
    setPhases((prev) => prev.filter((phase) => phase.id !== phaseId))
  }

  const handleAddPhase = () => {
    setPhases((prev) => [
      ...prev,
      {
        id: `phase-${Date.now()}`,
        name: '',
        isExpanded: true,
        items: [],
      },
    ])
  }

  const handleUpdateStandaloneItem = (itemId: string, updates: Partial<QuoteItem>) => {
    setStandaloneItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...updates } : item))
    )
  }

  const handleAddStandaloneItem = (itemType: 'role' | 'product') => {
    setStandaloneItems((prev) => [
      ...prev,
      {
        id: `standalone-${Date.now()}`,
        name: '',
        hoursOrUnits: 0,
        costPerUnit: 0,
        billPerUnit: 0,
        profit: 0,
        itemType,
      },
    ])
  }

  const handleDeleteStandaloneItem = (itemId: string) => {
    setStandaloneItems((prev) => prev.filter((item) => item.id !== itemId))
  }

  return (
    <>
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        paddingBottom="s20"
      >
        <Text size="400" weight="semibold" color="grey1000">
          {i18n.t('finance.quotes.quote_phases_table.title')}
        </Text>
      </Box>
      <Table autoWidth>
        <Header />
        <Body
          phases={phases}
          standaloneItems={standaloneItems}
          currency={legalEntityMainCurrency}
          onTogglePhaseExpand={handleTogglePhaseExpand}
          onUpdatePhase={handleUpdatePhase}
          onUpdateItem={handleUpdateItem}
          onUpdateStandaloneItem={handleUpdateStandaloneItem}
          onAddPhase={handleAddPhase}
          onAddItem={handleAddItem}
          onAddStandaloneItem={handleAddStandaloneItem}
          onDeletePhase={handleDeletePhase}
          onDeleteItem={handleDeleteItem}
          onDeleteStandaloneItem={handleDeleteStandaloneItem}
        />
      </Table>
    </>
  )
}

export default QuotePhasesTable
